import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback
} from "react";
import * as d3 from "d3";
import { animationDurationToMoveCorrectAnswer, animationDurationToShowQuestion } from "../../../../config";
import { CircleButton } from '../../../../components/elements/CircleButton';
import { motion } from "framer-motion";
import Sound from '../../../../components/global/Sound';
import { getCircleComponent } from "../../../../hooks/useCircleButtonColor";

const Line = ({ thickness, points, color }) => {
  const line = useMemo(() => {
    return d3
      .line()
      .x((d) => d.x)
      .y((d) => d.y)
  }, []);

  return (
    <path
      d={line(points)}
      style={{
        zIndex: '0 !important',
        stroke: color,
        strokeWidth: thickness,
        strokeLinejoin: "round",
        strokeLinecap: "round",
        fill: "none"
      }}
    />
  );
};

let showHint;

export const MouseDraw = ({ state, thickness, calcGapX, handleScore, calcGapY, answers, images, numbers, anim, screenSize, answerSound, hintState }) => {
  const coord = {}
  const colors = useRef(getCircleComponent(answers));
  const [drawing, setDrawing] = useState(false);
  const [currentLine, setCurrentLine] = useState({ thickness, points: [] });
  const [lines, setLines] = useState([]);
  const rightCount = useRef(0);
  const [animate, setAnimate] = useState(anim);
  const [answerCoord, setAnswerCoord] = useState();
  const soundWrongRef = useRef();
  const soundRef = useRef();
  const answer = useRef();
  const selectedItems = useRef([]);
  const [hint, setHint] = useState(0);
  const drawingAreaRef = useRef();
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [end, setEnd] = useState({ x: 0, y: 0 });
  const [time, setTime] = useState(0);

  useEffect(() => {
    showHint = true;
    const start = document.querySelector('.hint');
    const end = document.querySelector('.drop' + answers[hint]);
    if (!start || !end) return;
    const startCoord = start.getBoundingClientRect();
    const endCoord = end.getBoundingClientRect();
    setStart({ x: startCoord.x, y: startCoord.y });
    setEnd({ x: endCoord.x + endCoord.width, y: endCoord.y });
    setTime(0);
  }, [hint, screenSize]);


  useEffect(() => {
    if (!hintState) {
      setTime(0);
      return;
    }
    if (time === 10) return;
    const id = setInterval(() => setTime(prev => prev + 1), 1000);
    return () => clearInterval(id);
  }, [time, hintState]);

  const mouseMove = useCallback((event) => {
    const [x, y] = d3.pointer(event);
    if (drawing) {
      setCurrentLine((line) => ({
        ...line,
        points: [...line.points, { x, y }]
      }));
    }
  }, [drawing]);

  const enableDrawing = () => {
    setCurrentLine({ thickness, points: [] });
    setDrawing(!drawing);
    answer.current = null
  }

  useEffect(() => {
    colors.current = getCircleComponent(answers);
    setAnimate(true);
    const answersCoord = {}
    const answerElements = document.querySelectorAll('.answer');
    answers.forEach((number, index) => {
      const el = answerElements[index]
      if (!el) return;
      answersCoord[number] = {
        x: el.offsetLeft,
        y: [el.offsetTop, el.offsetHeight + el.offsetTop]
      }
    })
    setAnswerCoord(answersCoord);
    if (lines.length) {
      const updateLines = [];
      selectedItems.current.forEach((num) => {
        const selected = answersCoord[num];
        const wrapper = document.querySelector('.wrapper');
        updateLines.push({ thickness, points: [{ x: wrapper.offsetLeft - 5, y: (selected.y[0] + selected.y[1]) / 2 }, { x: coord[num].x[1] + 5, y: (coord[num].y[0] + coord[num].y[1]) / 2 }] })
      });
      setLines(updateLines);
    }

  }, [numbers, state, screenSize]);

  const disableDrawing = () => {
    if (!currentLine.points.length) return;
    const start = currentLine.points[0];
    const end = currentLine.points[currentLine.points.length - 1];
    let selected;
    let num;
    for (let key in answerCoord) {
      const elem = answerCoord[key];
      if (start.y >= elem.y[0] && start.y <= elem.y[1]) {
        selected = elem;
        num = key
      }
    }
    if (selected) {
      const wrapper = document.querySelector('.wrapper');
      if (end.x <= coord[num].x[1] + 10 && end.x >= coord[num].x[0] - 10) {
        if (end.y <= coord[num].y[1] + 5 && end.y >= coord[num].y[0] - 5) {
          setDrawing(false);
          setCurrentLine({ thickness, points: [] });
          setLines((lines) => [...lines, { thickness, points: [{ x: wrapper.offsetLeft - 5, y: (selected.y[0] + selected.y[1]) / 2 }, { x: coord[num].x[1] + 5, y: (coord[num].y[0] + coord[num].y[1]) / 2 }] }]);
          selectedItems.current.push(num);
          showHint = false;
          setHint(prev => prev + 1);
          rightCount.current += 1;
          answerSound + "" == "true" && soundRef.current.play();
          //alert('right')
          if (rightCount.current === 4) {
            handleScore();
            setTimeout(() => {
              setLines([]);
              setHint(0);
              selectedItems.current = [];
            }, animationDurationToMoveCorrectAnswer * 1000);
            rightCount.current = 0;
          }
        } else {
          soundWrongRef.current.currentTime = 0;
          answerSound + "" == "true" && soundWrongRef.current.play();
          answer.current = num;
        }
      } else {
        if (answerSound + "" == "true") {
          soundWrongRef.current.currentTime = 0;
          soundWrongRef.current.play();
        }
      }
    }
    setDrawing(false);
    setCurrentLine({ thickness, points: [] });
  }

  useEffect(() => {
    const area = d3.select(drawingAreaRef.current);
    area.on("pointermove", mouseMove);
    return () => area.on("pointermove", null);
  }, [mouseMove]);


  return (
    <>
      <svg
        ref={drawingAreaRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'all', zIndex: 100 }}
      ><g
        ref={drawingAreaRef}
        onPointerDown={enableDrawing}
        onPointerUp={disableDrawing}
      >
          <rect
            x={0}
            y={0}
            width="100%"
            height="100%"
            style={{ fill: "transparent" }}
          />


          {lines.map((line, i) => (
            <>
              {line.points.length && <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
                <circle cx={line.points[0].x} cy={line.points[0].y} r="4" strokeWidth="2" fill="#87B43F" />
              </svg>}
              {line.points.length && <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
                <circle cx={line.points[1].x} cy={line.points[1].y} r="4" strokeWidth="2" fill="#87B43F" />
              </svg>}
              <Line thickness={line.thickness} color={"#87B43F"} points={line.points} key={i} />
            </>

          ))}
          {drawing && <Line thickness={currentLine.thickness} color={"red"} points={currentLine.points} />}
        </g>
      </svg>
      {
        numbers.map((number, index) => (
          <div
            onPointerUp={disableDrawing}
            ref={el => {
              if (!el) return;
              coord[number] = {
                x: [el.offsetLeft, el.offsetLeft + el.offsetWidth],
                y: [el.offsetTop, el.offsetHeight + el.offsetTop]
              }

            }}
            key={number}
            style={{ marginTop: calcGapY(state, 'rect', 4) + "px" , zIndex: 110}}
            className={`${'drop' + number} w-[200px] xs:w-[240px] sm:w-[276px] z-[70] bg-[#FFF] overflow-hidden ${state ? 'md:w-[292px] lg:w-[362px] xl:w-[508px] md:h-[53px] lg:h-[64px] xl:h-[88px]' : 'md:w-[348px] lg:w-[463px] xl:w-[566px] md:h-[61px] lg:h-[80px] xl:h-[97px]'} sm:h-[51px] xs:h-[49px] h-[36px] rounded-[10px] md:rounded-[12px] border-[2px] border-[#BF3037] flex items-center`}>
            {
              [...new Array(number)].map((item, i) => (
                <motion.div
                  animate={{ y: animate ? [Math.random() * 500 * 0, 0] : [0, 0] }}
                  transition={{ duration: animationDurationToShowQuestion * (i + 1) / number, type: "tween" }}
                  key={i}
                  style={{
                    backgroundImage: `url(/backGrounds/counting/${images[index]}.svg)`,
                    backgroundSize: '100% 100%', backgroundRepeat: "no-repeat",
                    marginLeft: calcGapX(state, number) - 1 + "px",
                  }}
                  className={`z-[60] ${state ? 'w-[27px] h-[27px] md:h-[30px] lg:w-[32px] lg:h-[38px] xl:w-[44px] xl:h-[52px]' : 'h-[27px] w-[27px] md:w-[30px] md:h-[35px] lg:w-[37px] lg:h-[44px] xl:w-[49px] xl:h-[58px]'}`}
                />
              ))
            }
          </div>
        ))
      }
      <div
        className={`wrapper absolute right-[20px] ${state ? ' md:ml-[76px] md:right-[16px] lg:right-[24px] xl:right-[20px] lg:ml-[130px] xl:ml-[150px]' : 'md:ml-[130px] md:right-[25px] lg:right-[30px] xl:right-[32px] lg:ml-[133px] xl:ml-[190px]'} h-full ml-[77px]`}>
        {answers.map((number, index) => (
          <motion.div
            onPointerDown={enableDrawing}
            animate={{ y: animate ? [0, 0] : [Math.random() * 500, 0], x: number == answer.current ? [-10, 0, 10, 0] : 0 }}
            transition={{ duration: animationDurationToShowQuestion * (index + 1) / 4, type: "tween" }}

            className={`relative answer ${hint === index ? "z-[41]" : "z-[40]"}  h-[51px] ${hint === index && "hint"} ${state ? 'md:h-[53px] lg:h-[64px] xl:h-[88px]' : 'md:h-[61px] lg:h-[80px] xl:h-[97px]'} select-none flex items-center justify-center`}
            style={{ marginTop: calcGapY(state, 'rect', answers.length) + "px" }}
            key={index}
          >
            <CircleButton isActive={number} color={colors.current[index]} >
              {number}
            </CircleButton>
            {hintState && showHint && time == 10 && hint === index && <motion.div
              animate={{ y: [0, end.y - start.y], x: [0, end.x - start.x] }}
              transition={{ duration: 2, type: "tween", ease: "linear", repeat: Infinity }}
              className="bg-[url('/backGrounds/hint.svg')] bg-[position:50%_50%] bg-contain bg-no-repeat absolute w-[50px] h-[50px] top-[50%] z-[1000]"></motion.div>}
          </motion.div>
        ))}
      </div>
      <Sound ref={soundRef} url={"/backGrounds/right_answer.wav"} loop={false} />
      <Sound ref={soundWrongRef} url={"/backGrounds/wrong_answer.wav"} loop={false} />
    </>

  )
}